// src/pages/SupportKnowledge.jsx
// Editor de la base de conocimiento del bot. Rediseño 2026-05-13. Lógica 1:1.
import React, { useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../services/api';

const Icon = ({ children, size = 16, stroke = 1.75 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth={stroke}
         strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        {children}
    </svg>
);
const I = {
    save: <Icon><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><path d="M17 21v-8H7v8M7 3v5h8" /></Icon>,
    rotate: <Icon><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M3 21v-5h5" /></Icon>,
    discard: <Icon><path d="M18 6 6 18" /><path d="m6 6 12 12" /></Icon>,
    info: <Icon size={14}><circle cx="12" cy="12" r="9" /><path d="M12 16v-4M12 8h.01" /></Icon>,
};

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
.sp-root {
    --sp-accent: var(--brand-700, #0a4aa8);
    --sp-accent-hover: var(--brand-800, #07357f);
    --sp-accent-ring: rgba(10, 74, 168, .12);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    -webkit-font-smoothing: antialiased;
    max-width: 1100px; margin: 0 auto; padding: 24px 20px 60px;
    color: var(--c-text-1);
}
.sp-root * { box-sizing: border-box; }
.sp-title { font-size: 24px; font-weight: 700; margin: 0 0 4px; letter-spacing: -0.01em; }
.sp-subtitle { color: var(--c-text-3); font-size: 13.5px; margin: 0 0 20px; line-height: 1.5; }
.sp-card {
    background: var(--c-card);
    border: 1px solid var(--c-border);
    border-radius: 14px;
    box-shadow: 0 1px 2px rgba(15,23,42,.04), 0 2px 8px rgba(15,23,42,.04);
    padding: 18px;
}
.sp-meta-bar {
    display: flex; align-items: center; gap: 10px; margin-bottom: 10px;
    flex-wrap: wrap; font-size: 12px; color: var(--c-text-4);
}
.sp-seed-pill {
    background: #fef3c7; color: #92400e;
    padding: 3px 9px; border-radius: 999px;
    font-size: 11px; font-weight: 600;
}
.sp-char-count { margin-left: auto; font-variant-numeric: tabular-nums; }
.sp-textarea {
    width: 100%; min-height: 480px;
    padding: 14px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 13px; line-height: 1.6;
    border: 1px solid var(--c-border);
    border-radius: 10px;
    background: var(--c-input-bg);
    color: var(--c-text-1);
    resize: vertical;
    outline: none;
    transition: border-color .15s, box-shadow .15s;
}
.sp-textarea:focus {
    border-color: var(--sp-accent);
    box-shadow: 0 0 0 3px var(--sp-accent-ring);
    background: var(--c-card);
}
.sp-actions {
    display: flex; gap: 8px; margin-top: 14px; flex-wrap: wrap;
}
.sp-btn {
    display: inline-flex; align-items: center; gap: 6px;
    border: 1px solid transparent; border-radius: 8px;
    padding: 9px 16px; font-size: 13px; font-weight: 600;
    cursor: pointer; font-family: inherit;
    transition: all .15s;
}
.sp-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.sp-btn-primary { background: var(--sp-accent); color: #fff; border-color: var(--sp-accent); box-shadow: 0 1px 3px rgba(10,74,168,.25); }
.sp-btn-primary:hover:not(:disabled) { background: var(--sp-accent-hover); border-color: var(--sp-accent-hover); transform: translateY(-1px); }
.sp-btn-ghost { background: transparent; color: var(--c-text-2); border-color: var(--c-border); }
.sp-btn-ghost:hover:not(:disabled) { background: var(--c-msg-bg); }
.sp-btn-danger { background: transparent; color: #dc2626; border-color: #fecaca; margin-left: auto; }
.sp-btn-danger:hover:not(:disabled) { background: var(--c-priority-urgent-bg); border-color: #dc2626; }

.sp-tips {
    margin-top: 16px; padding: 14px 16px;
    background: var(--c-status-open-bg);
    border: 1px solid #93c5fd;
    border-radius: 10px;
    font-size: 12.5px; line-height: 1.55;
    color: #1e3a8a;
}
[data-bs-theme="dark"] .sp-tips { background: rgba(37,99,235,.08); border-color: rgba(37,99,235,.3); color: #93c5fd; }
.sp-tips strong { display: flex; align-items: center; gap: 6px; }
.sp-tips ul { margin: 8px 0 0 22px; padding: 0; }
.sp-tips kbd {
    display: inline-block; padding: 1px 6px;
    background: rgba(255,255,255,.5); border: 1px solid rgba(0,0,0,.15);
    border-radius: 4px; font-family: ui-monospace, SFMono-Regular, monospace;
    font-size: 11px;
}
[data-bs-theme="dark"] .sp-tips kbd { background: rgba(0,0,0,.3); border-color: rgba(255,255,255,.2); }
`;

export default function SupportKnowledge() {
    const [body, setBody]       = useState('');
    const [meta, setMeta]       = useState(null);
    const [original, setOriginal] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving]   = useState(false);
    const taRef = useRef(null);

    const dirty = body !== original;

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/support/knowledge');
            setBody(data.body || '');
            setOriginal(data.body || '');
            setMeta(data);
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo cargar la KB');
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!body.trim()) { toast.warn('La KB no puede quedar vacía'); return; }
        setSaving(true);
        try {
            const { data } = await api.put('/support/knowledge', { body });
            setBody(data.body || '');
            setOriginal(data.body || '');
            setMeta(data);
            toast.success('KB guardada — el bot la usa en los próximos turnos.');
        } catch (e) {
            toast.error(e.response?.data?.error || 'Error al guardar');
        } finally { setSaving(false); }
    };

    const reset = async () => {
        if (!window.confirm('¿Restaurar el contenido original (seed)? Tu versión actual se reemplaza.')) return;
        setSaving(true);
        try {
            const { data } = await api.post('/support/knowledge/reset');
            setBody(data.body || '');
            setOriginal(data.body || '');
            setMeta(data);
            toast.success('KB restaurada al baseline');
        } catch (e) {
            toast.error(e.response?.data?.error || 'Error al restaurar');
        } finally { setSaving(false); }
    };

    useEffect(() => {
        const handler = (e) => {
            const isSave = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's';
            if (!isSave) return;
            e.preventDefault();
            if (dirty && !saving) save();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [body, dirty, saving]);

    return (
        <>
            <style>{CSS}</style>
            <div className="sp-root">
                <h1 className="sp-title">Conocimiento del bot</h1>
                <p className="sp-subtitle">
                    Este texto se inyecta como instrucciones del bot antes de cada conversación, junto a los datos del cliente.
                    Edita con cuidado: los cambios se aplican en ~1 minuto (cache).
                </p>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: 60 }}><Spinner animation="border" variant="primary" /></div>
                ) : (
                    <>
                        <div className="sp-card">
                            <div className="sp-meta-bar">
                                {meta?._seed && (
                                    <span className="sp-seed-pill">Seed inicial — sin guardar todavía</span>
                                )}
                                {meta?.updatedAt && !meta?._seed && (
                                    <span>
                                        Última edición: {new Date(meta.updatedAt).toLocaleString()}
                                        {meta.updatedBy?.name && <> · por <strong>{meta.updatedBy.name}</strong></>}
                                    </span>
                                )}
                                <span className="sp-char-count">
                                    {body.length.toLocaleString()} / 50.000 caracteres
                                </span>
                            </div>

                            <textarea
                                ref={taRef}
                                value={body}
                                onChange={e => setBody(e.target.value)}
                                spellCheck={false}
                                className="sp-textarea"
                                placeholder="Escribe aquí las instrucciones del bot…"
                            />

                            <div className="sp-actions">
                                <button type="button" className="sp-btn sp-btn-primary" onClick={save} disabled={!dirty || saving}>
                                    {saving ? <Spinner size="sm" animation="border" /> : <>{I.save} {dirty ? 'Guardar cambios' : 'Sin cambios'}</>}
                                </button>
                                <button type="button" className="sp-btn sp-btn-ghost" onClick={() => setBody(original)} disabled={!dirty || saving}>
                                    {I.discard} Descartar
                                </button>
                                <button type="button" className="sp-btn sp-btn-danger" onClick={reset} disabled={saving}>
                                    {I.rotate} Restaurar baseline
                                </button>
                            </div>
                        </div>

                        <div className="sp-tips">
                            <strong>{I.info} Sugerencias para editar</strong>
                            <ul>
                                <li>Mantén las reglas de comportamiento (escalar ante duda, no inventar datos).</li>
                                <li>Para agregar una FAQ, copia el formato existente (pregunta + respuesta).</li>
                                <li>El bot recibe además los datos del cliente (nombre, marca, país, ruta) — no los repitas acá.</li>
                                <li>Atajo: <kbd>Ctrl</kbd> / <kbd>Cmd</kbd> + <kbd>S</kbd> guarda.</li>
                            </ul>
                        </div>
                    </>
                )}
            </div>
        </>
    );
}
